import _ from "lodash";
import React, { useMemo } from "react";
import classes from "./CriteriaRatingV2.scss";
import classNames from "classnames";
import DotLoadingIcon from "UIComponents/DotLoadingIcon";
import { useI18n } from "Hooks";
import {
  IB_PYP_ELEMENT_TYPE_DP,
  CRITERIA_RUBRIC_FINAL_GRADE,
} from "Projects/Constants/stringConstants";
import { onEnterOrSpaceKeyDown } from "Utils";
import { useDSContext } from "@toddle-edu/ds-web";

const getComputedAndOverRiddenRating = ({
  ratedCriteria,
  updatedId,
  leafNode,
}) => {
  const res = _.reduce(
    ratedCriteria,
    (res, item) => {
      if (
        item?.ibPYPElementId == updatedId &&
        item?.academicCriteriaSet?.criteriaType ==
          leafNode?.academicCriteriaSetCriteriaType
      ) {
        if (item?.isComputedRating) res["computedRating"] = item;
        else res["overRiddenRating"] = item;
      }
      return res;
    },
    { computedRating: {}, overRiddenRating: {} }
  );
  return res;
};

//Use this version when you only have highest Mark and don't have the options for Criteria Rating.
const CriteriaRatingV2 = ({
  highestMark,
  id,
  isEditable,
  ratedCriteria,
  width,
  onSelectMark,
  type,
  highestGrade,
  ratingType = "NUMBERS",
  leafNode = {},
  containerStyle = {},
  customSelectedRatingElement,
  customRatingElementClass,
  isGradeUnderComputation = false,
  showGradeComputationLoader = false,
  selectedCriteriaRef = {},
  showEmptyValueInSelection = false,
  testId = "UIComponents",
}) => {
  const { t } = useI18n(["common"]);
  const isFinalGradeDynamic = _.get(leafNode, "isFinalGradeDynamic", false);
  const { dsVersion } = useDSContext();
  const isDSV2 = dsVersion === "2.0";

  const ratingArray = useMemo(() => {
    switch (ratingType) {
      case "NUMBERS": {
        const arr = [];
        if (showEmptyValueInSelection) arr.push("-");
        return _.concat(arr, ..._.range(0, highestMark + 1));
      }
      case "ALPHABET": {
        const arr = [];
        if (showEmptyValueInSelection) arr.push("-");
        for (var i = 65; i <= highestGrade?.charCodeAt(0); i++) {
          arr.push(String.fromCharCode(i));
        }
        return arr;
      }
    }
  }, [highestGrade, highestMark, ratingType]);

  const getUpdatedId = ({ leafNode, id }) => {
    // If ibPYPElementType can be either of PROJECT_GROUP or DP_CRITERIA, then we determine whether to send id or null in parent component
    if (isFinalGradeDynamic) {
      return id;
    }

    if (leafNode?.academicCriteriaSetCriteriaType && leafNode?.ibPYPElementId) {
      return leafNode?.ibPYPElementId;
    }

    switch (_.get(leafNode, "criteriaType", "")) {
      case CRITERIA_RUBRIC_FINAL_GRADE:
        return null;
      default:
        return id;
    }
  };

  const updatedId = getUpdatedId({ leafNode, id });

  const ratedCriteriaStringified = JSON.stringify(ratedCriteria);
  const leafNodeStringified = JSON.stringify(leafNode);

  const selectedValue = useMemo(() => {
    if (isFinalGradeDynamic) {
      return _.find(ratedCriteria, {
        criteriaType: CRITERIA_RUBRIC_FINAL_GRADE,
        ibPYPElementId:
          _.get(leafNode, "type", "") === IB_PYP_ELEMENT_TYPE_DP
            ? null
            : updatedId,
      })?.value;
    } else {
      const leafNodeCriteriaType = _.get(
        leafNode,
        "academicCriteriaSetCriteriaType",
        ""
      );

      const searchCriteria = {
        ibPYPElementId: updatedId,
      };
      let updatedCriteria = ratedCriteria;

      if (leafNodeCriteriaType) {
        if (_.some(ratedCriteria, "criteriaType")) {
          updatedCriteria = _.filter(ratedCriteria, {
            criteriaType: leafNodeCriteriaType,
          });
        } else {
          updatedCriteria = _.filter(ratedCriteria, {
            academicCriteriaSet: { criteriaType: leafNodeCriteriaType },
          });
        }
      }

      return _.find(updatedCriteria, searchCriteria)?.value;
    }
  }, [
    isFinalGradeDynamic,
    updatedId,
    ratedCriteriaStringified,
    leafNodeStringified,
  ]);

  const doesSelectedValueExists =
    !_.isNil(selectedValue) && _.size(selectedValue) > 0;

  const getAcademicCriteriaSetId = ({ leafNode }) => {
    // If ibPYPElementType can be either of PROJECT_GROUP or DP_CRITERIA, then always send academicCriteriaSetId
    if (isFinalGradeDynamic) {
      return _.get(leafNode, "academicCriteriaSet.id", null);
    }

    switch (_.get(leafNode, "criteriaType", "")) {
      case CRITERIA_RUBRIC_FINAL_GRADE:
        return _.get(leafNode, "id", "");
      default:
        return _.get(leafNode, "academicCriteriaSet.id", null);
    }
  };

  const getSubText = () => {
    if (leafNode?.academicCriteriaSetCriteriaType) {
      const {
        computedRating,
        overRiddenRating,
      } = getComputedAndOverRiddenRating({
        ratedCriteria,
        updatedId,
        leafNode,
      });

      const computedGradeClasses = classNames({
        [classNames(classes.computedGrade, "text-label-4")]: !isDSV2,
        [classNames(classes.computedGradeV2, "text-label-s")]: isDSV2,
      });

      return !_.isEmpty(computedRating) &&
        !_.isEmpty(overRiddenRating) &&
        !_.isEqual(computedRating?.value, overRiddenRating?.value) ? (
        <div className={computedGradeClasses}>
          <sup>*</sup>
          {t("common:calculated_grade")}: {computedRating?.value}
        </div>
      ) : null;
    } else return null;
  };

  const getComp = ({ value, index }) => {
    const academicCriteriaSetId = getAcademicCriteriaSetId({ leafNode });

    let isSelected =
      !selectedValue && showEmptyValueInSelection && value == "-"
        ? true
        : selectedValue == value;

    let finalRatingToShow = {};
    let doesComputedRatingExist = false;
    let isUpdatedValueSameAsComputedValue = false;

    if (leafNode?.academicCriteriaSetCriteriaType) {
      const {
        computedRating,
        overRiddenRating,
      } = getComputedAndOverRiddenRating({
        ratedCriteria,
        updatedId,
        leafNode,
      });

      finalRatingToShow = !_.isEmpty(overRiddenRating)
        ? overRiddenRating
        : computedRating;

      doesComputedRatingExist = !_.isEmpty(computedRating);
      isUpdatedValueSameAsComputedValue = value == computedRating?.value;

      isSelected =
        !selectedValue && showEmptyValueInSelection && value == "-"
          ? true
          : finalRatingToShow?.value == value;
    }

    const ratingElementClass = classNames(
      { "text-body-3": !isDSV2 },
      { "text-body-s": isDSV2 },
      { [classes.ratingElement]: true },
      { [classes.ratingElementV2]: isDSV2 },
      { [classes.ratingElementDisabled]: !isEditable },
      { [classes.ratingElementDisabledV2]: !isEditable && isDSV2 },
      { [classes.selectedRatingElement]: isSelected },
      { [classes.selectedRatingElementV2]: isSelected && isDSV2 },
      {
        [customSelectedRatingElement]:
          isSelected && !_.isEmpty(customSelectedRatingElement),
      },
      { [customRatingElementClass]: !_.isEmpty(customRatingElementClass) }
    );

    const onClickCriteriaRating = e => {
      e.stopPropagation();
      isEditable &&
        onSelectMark({
          mark: value,
          isSelected,
          id: updatedId,
          type,
          academicCriteriaSetId,
          nodeDetails: leafNode,
          isComputedRatingToBeUpdated: finalRatingToShow?.isComputedRating,
          isFinalGradeDynamic,
          doesComputedRatingExist,
          isUpdatedValueSameAsComputedValue,
          academicCriteriaSetCriteriaType:
            leafNode?.academicCriteriaSetCriteriaType,
        });
    };

    return (
      <div
        className={ratingElementClass}
        key={value}
        onClick={onClickCriteriaRating}
        tabIndex={0}
        onKeyDown={e => onEnterOrSpaceKeyDown(e, onClickCriteriaRating)}
        ref={ref => {
          if (
            (doesSelectedValueExists && isSelected) ||
            (!doesSelectedValueExists && index === 0)
          ) {
            selectedCriteriaRef.current = ref;
          }
        }}
        data-test-id={`${testId}-criteriaRating-${id}-${value}`}
        data-is-selected={isSelected}
      >
        {value}
      </div>
    );
  };

  return (
    <div
      className={classes.ratingElementsContainer}
      style={{ width, ...containerStyle }}
    >
      {isGradeUnderComputation && showGradeComputationLoader ? (
        <div className={classes.loadingContainer}>
          <DotLoadingIcon />
        </div>
      ) : (
        <>
          <div className={classes.ratingElementInnerContainer}>
            {_.map(ratingArray, (rating, index) =>
              getComp({ value: rating, index })
            )}
          </div>
          {getSubText()}
        </>
      )}
    </div>
  );
};

export default CriteriaRatingV2;
